import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface Props {
  content: string;
}

function normalizeSpacing(content: string): string {
  // LLMs sometimes emit "sentence.\nNext" or "term:\nNext" without a space.
  // ReactMarkdown then treats it as a single paragraph and the UI looks
  // unprofessional. Normalize common cases to "sentence. Next" / "term: Next".
  return content
    .replace(/\r\n/g, "\n")
    .replace(/([.!?])([A-ZÅÄÖ])/g, "$1 $2")
    .replace(/:([A-Za-zÅÄÖåäö0-9])/g, ": $1")
    .replace(/([.!?])\s*\n\s*([A-ZÅÄÖ])/g, "$1 $2")
    .replace(/:\s*\n\s*([A-Za-zÅÄÖåäö0-9])/g, ": $1");
}

export function ChatMessageMarkdown({ content }: Props) {
  const normalized = normalizeSpacing(content);
  return (
    <div className="text-sm leading-relaxed break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          // Let ReactMarkdown own whitespace rules; preserving raw newlines makes
          // "sentence.\nNext" lose the expected space and looks unprofessional.
          p: ({ children }) => (
            <p className="leading-relaxed whitespace-pre-line mb-3 last:mb-0">
              {children}
            </p>
          ),
          ul: ({ children }) => (
            <ul className="list-disc pl-5 space-y-1 my-2">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="list-decimal pl-5 space-y-1 my-2">{children}</ol>
          ),
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,
          h1: ({ children }) => (
            <h3 className="text-sm font-semibold mt-3 mb-1.5">{children}</h3>
          ),
          h2: ({ children }) => (
            <h4 className="text-sm font-semibold mt-3 mb-1.5">{children}</h4>
          ),
          h3: ({ children }) => (
            <h5 className="text-sm font-semibold mt-3 mb-1.5">{children}</h5>
          ),
          code: ({ children }) => (
            <code className="rounded bg-surface-elevated px-1 py-0.5 text-[12px]">
              {children}
            </code>
          ),
          pre: ({ children }) => (
            <pre className="overflow-x-auto rounded border border-border bg-surface-elevated p-2 text-[12px]">
              {children}
            </pre>
          ),
          a: ({ href, children }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              className="underline underline-offset-2 text-primary hover:text-primary/80"
            >
              {children}
            </a>
          ),
        }}
      >
        {normalized}
      </ReactMarkdown>
    </div>
  );
}
